/**
 * Maps a breakdown block's `bundle_index` to the focus editor's target.
 *
 * The backend deliberately returns only `bundle_index` — replicating the
 * ordinal rule server-side would create a second matched pair to keep in
 * lockstep (the slugify problem). The rule lives here, mirroring
 * `blockSlots.ts`: ordinal counts same-category blocks EARLIER in the
 * array, so repeated categories number 0, 1, 2… in array order.
 */
import type { PersonaBundleBlock } from '@/types/personaWizard'
import type { FocusTarget } from './FocusedBlockContext'

export function focusTargetForBundleIndex(
  blocks: PersonaBundleBlock[],
  bundleIndex: number,
): FocusTarget | null {
  const block = blocks[bundleIndex]
  if (!block) return null
  const ordinal = blocks
    .slice(0, bundleIndex)
    .filter((b) => b.category === block.category).length
  return { index: bundleIndex, category: block.category, ordinal }
}

/**
 * Resolves a breakdown block's `bundle_index` to a focus-editor target in a
 * DIFFERENT array than the one the breakdown was computed from.
 *
 * The composed-prompt breakdown always describes the SAVED bundle
 * (`serverBundle`), but the focus editor always indexes into the LIVE
 * bundle (`draftBundle ?? serverBundle`). Those two arrays can diverge —
 * blocks inserted, removed, or reordered in the draft — so re-applying the
 * breakdown's absolute `bundle_index` to the live array is meaningless: it
 * can silently land on a different block than the one the user clicked
 * "Edit" on (task-10 finding 1).
 *
 * The fix is to route through a STABLE identity instead of a raw index:
 * `(category, ordinal)` — e.g. "the 2nd trait block" — survives edits that
 * don't touch that category. We resolve `bundleIndex` against `savedBlocks`
 * to get that identity, then locate the same identity in `liveBlocks`.
 *
 * Returns null when `liveBlocks` has no block at that identity (the user
 * deleted it in the draft). Callers MUST treat null as "cannot open" and
 * must NOT fall back to any other block — silently opening unrelated
 * content is the exact bug this function exists to prevent.
 */
export function focusTargetAcrossSnapshots(
  savedBlocks: PersonaBundleBlock[],
  liveBlocks: PersonaBundleBlock[],
  bundleIndex: number,
): FocusTarget | null {
  const saved = focusTargetForBundleIndex(savedBlocks, bundleIndex)
  if (!saved) return null
  let ordinal = 0
  for (let index = 0; index < liveBlocks.length; index++) {
    if (liveBlocks[index].category !== saved.category) continue
    if (ordinal === saved.ordinal) {
      return { index, category: saved.category, ordinal }
    }
    ordinal++
  }
  return null
}

/** The block at a (category, ordinal) identity, or null. Same ordinal rule as above. */
export function blockAtIdentity(
  blocks: PersonaBundleBlock[],
  category: string,
  ordinal: number,
): PersonaBundleBlock | null {
  let seen = 0
  for (const b of blocks) {
    if (b.category !== category) continue
    if (seen === ordinal) return b
    seen++
  }
  return null
}
